/**
 * @file: useDeliveryCheckMediaController.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { PredefinedAnnotationTags } from '@euler/app/flows/media/annotation/functions/tagPresets';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { useDeliveryCheckContext } from '@euler/app/flows/task/functions/task-detail/delivery-check/DeliveryCheckContext';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  DeliveryCheckSceneType,
  ImageAnnotationScene,
} from '@euler/model/enum';
import { mimeTypeFromFileUri, withConfirmation } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useCallback } from 'react';
import { Subject } from 'rxjs';

export const useDeliveryCheckMediaController = (options: {
  changed$: Subject<string>;
}) => {
  const appNavigation = useAppNavigation();

  const { changed$ } = options;
  const { taskManager, detail, profile } = useTaskContext();
  const { manager } = useDeliveryCheckContext();

  const [staged] = useBehaviorSubject(manager.staged$);

  const {
    manager: { sceneType },
  } = useDeliveryCheckContext();

  const takeMedia = useMediaCaptureWithAnnotation({
    scene:
      manager.sceneType === DeliveryCheckSceneType.CompletionCheck
        ? ImageAnnotationScene.CompletionDeliveryCheck
        : ImageAnnotationScene.SamplingDeliveryCheck,
  });

  const onTakeMedia = usePersistFn(
    (key: string, allowChooseMediaFromGallery: boolean) => {
      const target = staged.items.find(x => x._key === key);
      takeMedia({
        mediaCaptureRequired: !allowChooseMediaFromGallery,
        tags: () =>
          sceneType === DeliveryCheckSceneType.CompletionCheck
            ? PredefinedAnnotationTags.deliveryCheck
            : PredefinedAnnotationTags.samplingCheck,
        defaultTag: () => ({
          name: target?.photoAnnotationTag ?? '检查合格',
          color: 'green',
        }),
        watermark:
          target != null
            ? (scale?: number) => (
                <TaskImageWatermark
                  licensePlateNo={detail.basicInfo.licensePlateNo}
                  title={target.title}
                  author={profile.user.name}
                  store={profile.store.name}
                  type={
                    sceneType === DeliveryCheckSceneType.CompletionCheck
                      ? 'delivery-check'
                      : 'sampling-check'
                  }
                  scale={scale}
                />
              )
            : undefined,
        uploadVideoFile: async (localFileUri, contentType, rotate) => {
          taskManager.uploadController.enqueue({
            localFileUri: localFileUri,
            contentType:
              contentType ?? mimeTypeFromFileUri(localFileUri, 'video/mp4'),
            type: 'video',
            parameters: {
              cover: true,
              blurhash: true,
              videoRotate: rotate,
            },
            context: {
              type:
                sceneType === DeliveryCheckSceneType.CompletionCheck
                  ? 'delivery-check'
                  : 'sampling-check',
              key,
            },
            listener: e => {
              if (e.type === 'finished') {
                changed$.next(key);
              }
            },
          });
        },
        handleImageAnnotationResults: async results => {
          for (const result of results) {
            taskManager.uploadController.enqueue({
              localFileUri: result.imageUri,
              contentType: 'image/jpeg',
              type: 'image',
              parameters: {
                cover: true,
                blurhash: true,
              },
              context: {
                type:
                  sceneType === DeliveryCheckSceneType.CompletionCheck
                    ? 'delivery-check'
                    : 'sampling-check',
                key,
                annotationMetadata: result.annotationMetadata,
              },
              // eslint-disable-next-line @typescript-eslint/no-loop-func
              listener: (e, task) => {
                if (e.type === 'finished') {
                  changed$.next(key);

                  // upload the original image.
                  const media = manager.findMedia(key, task.key);

                  if (!media) return;

                  if (media.annotationMetadata?.origin_uri) return;

                  if (result.originalImageUri != null) {
                    taskManager.largeMediaUploadController.enqueue({
                      localFileUri: result.originalImageUri,
                      contentType: 'image/jpeg',
                      type: 'image',
                      parameters: {
                        blurhash: true,
                      },
                      onSuccess: ({ result: { url } }) => {
                        manager.produceItem(
                          key,
                          (draft, original) => {
                            const index = original.medias.findIndex(
                              x => x._key === media._key,
                            );
                            const metadata =
                              draft.medias[index]?.annotationMetadata;
                            if (metadata) {
                              metadata.origin_uri = url;
                            }
                          },
                          { ignoreError: true },
                        );
                      },
                    });
                  }
                }
              },
            });
          }
        },
      });
    },
  );

  const onDeleteMedia = useCallback(
    async (key: string, media: MediaInfo) => {
      const label = media.type === 'image' ? '图片' : '视频';
      const confirmed = await withConfirmation({
        title: '确认',
        message: `是否确认删除该${label}?`,
        confirmButtonText: '是',
        cancelButtonText: '否',
      });
      if (confirmed) {
        manager.removeMedia(key, media.key);
        changed$.next(key);
      }
    },
    [changed$, manager],
  );

  const onEditMedia = useCallback(
    (key: string, mediaInfo: MediaInfo) => {
      const target = staged.items.find(x => x._key === key);
      const mediaStaged = manager.findMedia(key, mediaInfo.key);
      if (!mediaStaged) return;
      const originalImageUri = mediaStaged.annotationMetadata?.origin_uri;
      appNavigation.navigate('_imageAnnotation', {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        imageUri: originalImageUri || mediaInfo.url,
        tags:
          sceneType === DeliveryCheckSceneType.CompletionCheck
            ? PredefinedAnnotationTags.deliveryCheck
            : PredefinedAnnotationTags.samplingCheck,
        annotationMetadata: originalImageUri
          ? mediaStaged.annotationMetadata
          : undefined,
        watermark:
          target != null
            ? (scale?: number) => (
                <TaskImageWatermark
                  licensePlateNo={detail.basicInfo.licensePlateNo}
                  title={target.title}
                  author={profile.user.name}
                  store={profile.store.name}
                  type={
                    sceneType === DeliveryCheckSceneType.CompletionCheck
                      ? 'delivery-check'
                      : 'sampling-check'
                  }
                  scale={scale}
                />
              )
            : undefined,
        onDone: results => {
          if (
            JSON.stringify(mediaStaged.annotationMetadata ?? null) ===
            JSON.stringify(results.annotationMetadata ?? null)
          ) {
            return;
          }
          taskManager.uploadController.enqueue({
            localFileUri: results.imageUri,
            contentType: 'image/jpeg',
            type: 'image',
            // simulateFailure: true,
            parameters: {
              cover: true,
              blurhash: true,
            },
            context: {
              type:
                sceneType === DeliveryCheckSceneType.CompletionCheck
                  ? 'delivery-check'
                  : 'sampling-check',
              key,
              mediaKey: mediaInfo.key,
            },
            listener: e => {
              if (e.type === 'finished') {
                if (mediaStaged.annotationMetadata?.origin_uri) return;

                // use the non-annotated media as the original url.
                if (mediaStaged.annotationMetadata == null) {
                  manager.produceItem(
                    key,
                    (draft, original) => {
                      const index = original.medias.findIndex(
                        x => x._key === mediaStaged._key,
                      );
                      const metadata = draft.medias[index]?.annotationMetadata;
                      if (metadata) {
                        metadata.origin_uri = mediaInfo.url;
                      }
                    },
                    { ignoreError: true },
                  );
                }
              }
            },
          });
          appNavigation.pop();
        },
      });
    },
    [
      appNavigation,
      detail.basicInfo.licensePlateNo,
      manager,
      profile.store.name,
      profile.user.name,
      staged.items,
      taskManager.uploadController,
      sceneType,
    ],
  );

  const onRetryMediaUpload = useCallback(
    (key: string, media: MediaInfo) => {
      if (media.source?.type !== 'local') return;
      taskManager.uploadController.enqueue({
        localFileUri: media.source.localFileUri,
        contentType: media.source.contentType,
        type: media.type,
        parameters: media.source.uploadParameters,
        context: {
          type:
            sceneType === DeliveryCheckSceneType.CompletionCheck
              ? 'delivery-check'
              : 'sampling-check',
          key,
          mediaKey: media.key,
        },
      });
    },
    [taskManager.uploadController, sceneType],
  );

  const onPreviewVideo = useCallback(
    (key: string, media: MediaInfo) => {
      const stagedMedia = manager.findMedia(key, media.key);
      if (
        stagedMedia &&
        stagedMedia._source.type === 'local' &&
        stagedMedia._source.state.status === 'uploaded'
      ) {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: stagedMedia._source.localFileUri,
        });
      } else {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: media.url,
        });
      }
    },
    [appNavigation, manager],
  );

  return {
    onTakeMedia,
    onDeleteMedia,
    onEditMedia,
    onRetryMediaUpload,
    onPreviewVideo,
  };
};
